/**
 * Meet people's words and numbers that every client shows (2026-11-01 Riley): one source, served to the apps and the
 * web by GET /api/app/config as `friends` (app-config-routes.js), so a topic renamed or a rule's sentence changed
 * reaches installs in people's hands without a release. The edge builds the served one from its own rules
 * (#edge/config/friends-config.js: friends.js FRIENDS, config.js ROOM); the web and the apps carry the same text
 * compiled in as their defaults and take the served one over it.
 *
 * Only what the server's rules decide or what changes without a layout change is here: the topics on offer, the
 * limits of a card and a topic room's door, and the sentences that say how Meet people works (who sees a card, how
 * long an introduction stays open). Buttons, step titles and the pronoun / want choices stay in each client.
 *
 *   topics   [{ label, tint }] — the chips on offer to everyone, in order; tint is the chip's ground (#RRGGBB)
 *   limits   topics_max, topic_max_chars, line_max_chars, name_max_chars (a card);
 *            room_topic_max_chars, room_intro_max_chars (a topic room's door)
 *   words    intro_open       the introduction page, under its three facts
 *            card_seen        the setup's card step, under the card
 *            card_shown       the setup's topics step, the ⓘ line
 *            topic_card_seen  a topic room's door, under the card
 *
 * Sentences are whole: a client shows them as they come, never fills a number in.
 */

export const FRIEND_TOPICS = Object.freeze([
  ['Music', '#FBE1CF'], ['Pets', '#E3ECE0'], ['TV shows', '#ECE6F6'], ['Cooking', '#FFF1C7'],
  ['Gardening', '#E3ECE0'], ['Books', '#E2EEF6'], ['Faith', '#FBE1CF'], ['Sports', '#E2EEF6'],
  ['Grandkids', '#FFF1C7'], ['Crafts', '#ECE6F6'], ['Road trips', '#E3ECE0'], ['Old movies', '#FBE1CF'],
].map(([label, tint]) => Object.freeze({ label, tint })));

const days = (n) => (n === 1 ? 'a day' : `${n} days`);

/** The served shape, from the rules' numbers. */
export function friendsConfig({
  topicsMax, topicMaxChars, lineMaxChars, nameMaxChars, roomTopicMaxChars, roomIntroMaxChars, introOpenDays,
}) {
  return {
    topics: FRIEND_TOPICS.map((t) => ({ ...t })),
    limits: {
      topics_max: topicsMax,
      topic_max_chars: topicMaxChars,
      line_max_chars: lineMaxChars,
      name_max_chars: nameMaxChars,
      room_topic_max_chars: roomTopicMaxChars,
      room_intro_max_chars: roomIntroMaxChars,
    },
    words: {
      intro_open: `This introduction stays open for ${days(introOpenDays)}.`,
      card_seen: 'The person you meet sees just this card. Our chats stay ours.',
      card_shown: 'Ember may also show your card to someone it thinks you’d get on with, and ask if they’d like to meet you.',
      topic_card_seen: 'The room sees just this card and what you write there. Ember may also show it to people it thinks would enjoy this topic, and ask if they’d like to join.',
    },
  };
}

/** What a client shows before the edge has answered (the rules as of 2026-11-01; a test keeps it equal to the edge's). */
export const FRIENDS_DEFAULT = Object.freeze(friendsConfig({
  topicsMax: 6, topicMaxChars: 30, lineMaxChars: 160, nameMaxChars: 24, roomTopicMaxChars: 60, roomIntroMaxChars: 300, introOpenDays: 3,
}));
